/* eslint-disable @typescript-eslint/no-explicit-any  */
import { ComponentRef, ViewRef } from '@angular/core';

import { Deferred } from '@frontend/sports/common/core/utils/deferred';
import { isPromise } from '@frontend/sports/common/core/utils/extended-types';
import { Observable, ReplaySubject } from 'rxjs';

import { ModalBackdropComponent } from './modal-backdrop.component';
import { ModalCoverComponent } from './modal-cover.component';
import { PopupClosingResult } from './modal-window.actions';

export class ContentRef<T = any> {
    constructor(
        public nodes: any[],
        public viewRef?: ViewRef,
        public componentRef?: ComponentRef<T>,
    ) {}
}

/**
 * A reference to an active (currently opened) modal. Instances of this class
 * can be injected into components passed as modal content.
 */
export class ActiveModal {
    /**
     * Can be used to close a modal, passing an optional result.
     */
    //lint rule disabled because the function is meant to be overriden
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
    close(reason?: any): void {}

    /**
     * Can be used to dismiss a modal, passing an optional reason.
     */
    //lint rule disabled because the function is meant to be overriden
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
    dismiss(reason?: any): void {}

    /**
     * A promise that is resolved when a modal is closed and rejected when a modal is dismissed.
     */
    result: Promise<any>;
}

/**
 * A reference to a newly opened modal.
 */
export class ModalRef<T = any> {
    private static readonly SPACE = ' ';
    private updateData$ = new ReplaySubject<object>(1);

    readonly newData$ = this.updateData$.asObservable();

    /**
     * The instance of component used as modal's content.
     * Undefined when a TemplateRef is used as modal's content.
     */
    get componentInstance(): any {
        if (this._contentRef?.componentRef) {
            return this._contentRef.componentRef.instance;
        }
    }

    /**
     * The instance of componentRef used as modal's content.
     * Undefined when a TemplateRef is used as modal's content.
     */
    get componentRef(): any {
        if (this._contentRef?.componentRef) {
            return this._contentRef.componentRef;
        }
    }

    /**
     * The modal window element.
     */
    get windowRef(): HTMLElement | null {
        if (this._windowCmptRef) {
            return this._windowCmptRef.location.nativeElement as HTMLElement;
        }

        return null;
    }

    constructor(
        private _windowCmptRef: ComponentRef<T> | null, // Make it generic
        private _contentRef: ContentRef<T> | null,
        private _backdropCmptRef?: ComponentRef<ModalBackdropComponent> | null,
        private _coverCmptRef?: ComponentRef<ModalCoverComponent> | null,
        private _beforeDismiss?: () => boolean | Promise<boolean>,
    ) {
        (_windowCmptRef?.instance as T & { dismiss: Observable<void> }).dismiss.subscribe(this.dismiss.bind(this));

        this.deferred = new Deferred();
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.deferred.promise.then(null, () => {});
    }

    updateTitle<T extends { title: string }>(title: string): void {
        const windowComponentInstance = this._windowCmptRef?.instance as unknown as T;

        if (windowComponentInstance) {
            windowComponentInstance.title = title;
        }
    }

    updateData(data: object): void {
        this.updateData$.next(data);
    }

    private deferred: Deferred<any>;

    /**
     * A promise that is resolved when a modal is closed and rejected when a modal is dismissed.
     */
    get result(): Promise<any> {
        return this.deferred.promise;
    }

    /**
     * Append a class name to class list.
     *
     * @param currentClasses
     */
    private appendClass(currentClasses: string, newClass: string): string {
        const classList = currentClasses.split(ModalRef.SPACE);
        classList.push(newClass);

        return classList.join(ModalRef.SPACE);
    }

    /**
     * Add a css class to the modal-window root
     */
    addWindowClass<T extends { windowClass: string }>(windowClass: string): this {
        const windowComponentInstance = this._windowCmptRef?.instance as unknown as T;
        if (windowComponentInstance) {
            windowComponentInstance.windowClass = this.appendClass(windowComponentInstance.windowClass, windowClass);
        }

        return this;
    }

    /**
     * Add a new class to the backdrop element.
     *
     * @param newClass the class to add.
     */
    addBackdropClass(newClass: string): this {
        const backdrop = this._backdropCmptRef && this._backdropCmptRef.instance;
        if (backdrop) {
            backdrop.backdropClass = this.appendClass(backdrop.backdropClass, newClass);
        }

        return this;
    }

    /**
     * Can be used to close a modal, passing an optional result.
     */
    close(result?: PopupClosingResult): void {
        if (this._windowCmptRef) {
            this.deferred.resolve(result);
            this._removeModalElements();
        }
    }

    private _dismiss(reason?: any): void {
        this.deferred.reject(reason);
        this._removeModalElements();
    }

    /**
     * Can be used to dismiss a modal, passing an optional reason.
     */
    dismiss(reason?: any): void {
        if (this._windowCmptRef) {
            if (!this._beforeDismiss) {
                this._dismiss(reason);
            } else {
                const dismiss = this._beforeDismiss();
                if (isPromise(dismiss)) {
                    dismiss.then(
                        (result: any) => {
                            if (result !== false) {
                                this._dismiss(reason);
                            }
                        },
                        // eslint-disable-next-line @typescript-eslint/no-empty-function
                        () => {},
                    );
                    //lint rule disabled to explicitly compare to false values, undefined or nulls should still dismiss
                } else if (dismiss !== false) {
                    this._dismiss(reason);
                }
            }
        }
    }

    private _removeModalElements(): void {
        const windowNativeEl = this._windowCmptRef?.location.nativeElement as HTMLElement;
        windowNativeEl.remove();
        this._windowCmptRef?.destroy();

        if (this._backdropCmptRef) {
            const backdropNativeEl = this._backdropCmptRef.location.nativeElement as HTMLElement;
            backdropNativeEl.remove();
            this._backdropCmptRef.destroy();
        }

        if (this._coverCmptRef) {
            const coverNativeEl = this._coverCmptRef.location.nativeElement as HTMLElement;
            coverNativeEl.remove();
            this._coverCmptRef.destroy();
        }

        if (this._contentRef && this._contentRef.viewRef) {
            this._contentRef.viewRef.destroy();
        }

        this._windowCmptRef = null;
        this._backdropCmptRef = null;
        this._contentRef = null;
        this._coverCmptRef = null;
    }
}
